import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { Toaster } from "./components/ui/sonner";
import EnrollmentPlatformsLauncher from "./components/EnrollmentPlatformsLauncher";
import ErrorBoundary from "./components/common/ErrorBoundary";
import DisclaimerGate from "./components/common/DisclaimerGate";
import { isKRRole } from "./lib/utils";
import { isAccountingRole } from "./lib/utils";

// Layout
import MainLayout from "./components/layout/MainLayout";

// Pages
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import AgenciesPage from "./pages/AgenciesPage";
import AgencyProfilePage from "./pages/AgencyProfilePage";
import AgentsPage from "./pages/AgentsPage";
import AgentProfilePage from "./pages/AgentProfilePage";
import RequestsPage from "./pages/RequestsPage";
import RequestDetailPage from "./pages/RequestDetailPage";
import ContractStatusImportPage from "./pages/ContractStatusImportPage";
import ContractStatusReviewPage from "./pages/ContractStatusReviewPage";
import ReleaseFollowUpsPage from "./pages/ReleaseFollowUpsPage";
import RequestContractPage from "./pages/RequestContractPage";
import CatalogPage from "./pages/CatalogPage";
import LevelCapsPage from "./pages/LevelCapsPage";
import TicketsPage from "./pages/TicketsPage";
import InvitationsPage from "./pages/InvitationsPage";
import OnboardingWorkflowsPage from "./pages/OnboardingWorkflowsPage";
import UrgentTasksPage from "./pages/UrgentTasksPage";
import ActionRequiredPage from "./pages/ActionRequiredPage";
import MyContractsPage from "./pages/MyContractsPage"; // eslint-disable-line no-unused-vars
import ProfilePage from "./pages/ProfilePage";
import AnnouncementsPage from "./pages/AnnouncementsPage";
import UsersPage from "./pages/UsersPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import RefundPolicyPage from "./pages/RefundPolicyPage";
import AuditLogPage from "./pages/AuditLogPage";
import ContractDuplicateReviewPage from "./pages/ContractDuplicateReviewPage";
import CarrierImportsPage from "./pages/accounting/CarrierImportsPage";
import CarrierImportDetailPage from "./pages/accounting/CarrierImportDetailPage";
import StatementsListPage from "./pages/accounting/StatementsListPage";
import StatementDetailPage from "./pages/accounting/StatementDetailPage";
import PaymentCenterPage from "./pages/accounting/PaymentCenterPage";
import EventsListPage from "./pages/events/EventsListPage";
import EventsCalendarDashboardPage from "./pages/events/EventsCalendarDashboardPage";
import RecognitionPortalPage from "./pages/RecognitionPortalPage";
import RecognitionProgramPage from "./pages/RecognitionProgramPage";
import RecognitionAdminPage from "./pages/RecognitionAdminPage";
import RecognitionProgramEditorPage from "./pages/RecognitionProgramEditorPage";
import EventWorkspacePage from "./pages/events/EventWorkspacePage";
import PaymentBatchesListPage from "./pages/accounting/PaymentBatchesListPage";
import PaymentBatchDetailPage from "./pages/accounting/PaymentBatchDetailPage";
import ManualAdjustmentsListPage from "./pages/accounting/ManualAdjustmentsListPage";
import LedgerExplorerPage from "./pages/accounting/LedgerExplorerPage";
import CommissionSchedulesPage from "./pages/accounting/CommissionSchedulesPage";
import UnreceivedCommissionPage from "./pages/accounting/UnreceivedCommissionPage";
import BankDepositsPage from "./pages/accounting/BankDepositsPage";
import AccountingHistoryPage from "./pages/accounting/AccountingHistoryPage";
import AccountingPlaceholderPage from "./pages/accounting/AccountingPlaceholderPage";
import ReimbursementsAdminPage from "./pages/accounting/ReimbursementsAdminPage";
import MyReimbursementsPage from "./pages/MyReimbursementsPage";
import CommunicationGovernancePage from "./pages/CommunicationGovernancePage";
import CalendarPage from "./pages/CalendarPage";
import PlatformSettingsPage from "./pages/PlatformSettingsPage";
import TwilioIntegrationPage from "./pages/TwilioIntegrationPage";
import VoiceSettingsPage from "./pages/VoiceSettingsPage";
import CallHistoryPage from "./pages/CallHistoryPage";
import CommunicationLogPage from "./pages/CommunicationLogPage";
import VoicemailInboxPage from "./pages/VoicemailInboxPage";
import VoiceSupervisionPage from "./pages/VoiceSupervisionPage";
import VoiceUsagePage from "./pages/VoiceUsagePage";
import CallFlowsPage from "./pages/CallFlowsPage";
import AcademyPage from "./pages/AcademyPage";
import CoursePlayerPage from "./pages/CoursePlayerPage";
import CourseBuilderPage from "./pages/CourseBuilderPage";
import CertificatePage from "./pages/CertificatePage";
import QuickBooksIntegrationPage from "./pages/QuickBooksIntegrationPage";
import ShopPage from "./pages/shop/ShopPage";
import CartPage from "./pages/shop/CartPage";
import CartCheckoutPage from "./pages/shop/CartCheckoutPage";
import AfterpayReturnPage from "./pages/shop/AfterpayReturnPage";
import MyOrdersPage from "./pages/shop/MyOrdersPage";
import SpecialOrderPayPage from "./pages/shop/SpecialOrderPayPage";
import AdminStorefrontPage from "./pages/admin/AdminStorefrontPage";
import DisclaimerConfigPage from "./pages/admin/DisclaimerConfigPage";
import AdminDeletionLogPage from "./pages/admin/AdminDeletionLogPage";
import SmsInboxPage from "./pages/SmsInboxPage";
import SmsBroadcastsPage from "./pages/SmsBroadcastsPage";
import PublicSmsOptInPage from "./pages/public/PublicSmsOptInPage";
import PublicAgentSmsPage from "./pages/public/PublicAgentSmsPage";
import ReportsPage from "./pages/ReportsPage";
import OperationsPage from "./pages/OperationsPage";
import NotificationPreferencesPage from "./pages/NotificationPreferencesPage";
import LedgerPage from "./pages/LedgerPage";
import StatementsPage from "./pages/StatementsPage";
import PaymentsPage from "./pages/PaymentsPage";
import ReconciliationPage from "./pages/ReconciliationPage";
import EFTPage from "./pages/EFTPage";
import MyPayoutsPage from "./pages/MyPayoutsPage";
import OverrideReconciliationPage from "./pages/OverrideReconciliationPage";
import CompensationPage from "./pages/CompensationPage";
import ManagersPage from "./pages/ManagersPage";
import KanbanBoardPage from "./pages/KanbanBoardPage";
import TraineesPage from "./pages/TraineesPage";
import MarketingPage from "./pages/marketing/MarketingPage";
import KnowledgePage from "./pages/KnowledgePage";
import TeamPage from "./pages/TeamPage";
import TeamMemberProfilePage from "./pages/TeamMemberProfilePage";
import MarketingTemplateEditor from "./pages/marketing/MarketingTemplateEditor";
import MarketingPersonalize from "./pages/marketing/MarketingPersonalize";
import MarketingRequestsPage from "./pages/marketing/MarketingRequestsPage";
import JarvisKeysPage from "./pages/admin/JarvisKeysPage";
import OrphanCarriersPage from "./pages/admin/OrphanCarriersPage";
import AepCertificationsAdminPage from "./pages/admin/AepCertificationsAdminPage";
import PublicAepPage from "./pages/PublicAepPage";
import PublicLayout from "./layouts/PublicLayout";
import HomePage from "./pages/public/HomePage";
import BecomeAgentPage from "./pages/public/BecomeAgentPage";
import PublicEventsPage from "./pages/public/PublicEventsPage";
import PublicEventDetailPage from "./pages/public/PublicEventDetailPage";
import PublicRafflePage from "./pages/public/PublicRafflePage";
import PublicShareEventRegisterPage from "./pages/public/PublicShareEventRegisterPage";
import AgentOnboardingPage from "./pages/public/AgentOnboardingPage";
import AgentApplicationPage from "./pages/public/AgentApplicationPage";
import ApplicationStatusPage from "./pages/public/ApplicationStatusPage";
import AgentInfoUpdatePage from "./pages/public/AgentInfoUpdatePage";
import AgencyInfoUpdatePage from "./pages/public/AgencyInfoUpdatePage";
import EmailPreferencesPage from "./pages/EmailPreferencesPage";
import ProspectsPage from "./pages/ProspectsPage";
import ReferralsPage from "./pages/ReferralsPage";
import AutoReminderSettingsPage from "./pages/AutoReminderSettingsPage";
import SeasonalThemesPage from "./pages/SeasonalThemesPage";
import KoraAnalyticsPage from "./pages/KoraAnalyticsPage";
import KoraTrainingPage from "./pages/KoraTrainingPage";
import BirthdaysPage from "./pages/BirthdaysPage";
import OperationsCenterPage from "./pages/OperationsCenterPage";
import LeadsPage from "./pages/LeadsPage";
import LeadDetailPage from "./pages/LeadDetailPage";
import ClientsPage from "./pages/ClientsPage";
import ClientDetailPage from "./pages/ClientDetailPage";
import LeadsImportPage from "./pages/LeadsImportPage";
import AppointmentsPage from "./pages/AppointmentsPage";
import LeadLandingPagesPage from "./pages/LeadLandingPagesPage";
import LeadLandingPageEditor from "./pages/LeadLandingPageEditor";
import AgentEventsKioskPage from "./pages/AgentEventsKioskPage";
import LandingConsentAdminPage from "./pages/LandingConsentAdminPage";
import PublicLandingPage from "./pages/PublicLandingPage";
import LeadsReportsPage from "./pages/LeadsReportsPage";
import SuppliesPage from "./pages/SuppliesPage";

// KR-only route guard — redirects non-KR users away from KR admin surfaces
// like `/catalog` (Carrier Management). Renders nothing while the auth
// context is loading (`user` is null) to avoid a flicker redirect.
function KrOnlyRoute({ children }) {
  const { user } = useAuth();
  if (!user) return null;
  if (!isKRRole(user)) return <Navigate to="/dashboard" replace />;
  return children;
}

// KR or Accounting guard — for Accounting admin surfaces.
function AccountingRoute({ children }) {
  const { user } = useAuth();
  if (!user) return null;
  if (!isKRRole(user) && !isAccountingRole(user)) return <Navigate to="/dashboard" replace />;
  return children;
}

// Wrapper that reads the current user from the auth context and hands
// it to DisclaimerGate. Kept as a tiny shim so we do not have to
// thread hooks through App itself.
function DisclaimerGateWithAuth() {
  const { user } = useAuth();
  return <DisclaimerGate user={user} />;
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/email-preferences/:token" element={<EmailPreferencesPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/refund-policy" element={<RefundPolicyPage />} />

              {/* KR Operations Center — standalone TV display, no sidebar/chrome */}
              <Route path="/operations-center" element={<OperationsCenterPage />} />

              {/* Public AEP self-service tracker — no login required. NPN acts as PIN. */}
              <Route path="/aep" element={<PublicAepPage />} />
              <Route path="/sms-opt-in" element={<PublicSmsOptInPage />} />
              <Route path="/agent-sms" element={<PublicAgentSmsPage />} />

              {/* Phase 27.43 — Agent Events kiosk for live-event lead capture. */}
              <Route path="/agentevents" element={<AgentEventsKioskPage />} />
              <Route path="/r/:token" element={<PublicRafflePage />} />
              <Route path="/onboard/:token" element={<AgentOnboardingPage />} />
              <Route path="/apply" element={<AgentApplicationPage />} />
              <Route path="/application-status/:token" element={<ApplicationStatusPage />} />
              <Route path="/update-info/:token" element={<AgentInfoUpdatePage />} />
              <Route path="/agency-update-info/:token" element={<AgencyInfoUpdatePage />} />

              {/* Phase 27.54 — Event Equipment & Supplies (KR-only STANDALONE
                  warehouse app; intentionally outside MainLayout so the CRM
                  sidebar/topbar does not surround this dedicated experience). */}
              <Route path="/supplies" element={<SuppliesPage />} />

              {/* Phase 27.31 — Public lead landing pages served at
                  /connect/:slug. Anonymous access, no layout. */}
              <Route path="/connect/:slug" element={<PublicLandingPage />} />

              {/* Phase 27.30.16 — Public share-link registration (agents / guests). No login. */}
              <Route path="/events/share/:token" element={<PublicShareEventRegisterPage />} />

              {/* Public marketing site (no auth required) */}
              <Route element={<PublicLayout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/become-an-agent" element={<BecomeAgentPage />} />
                <Route path="/events" element={<PublicEventsPage />} />
                <Route path="/events/:id" element={<PublicEventDetailPage />} />
              </Route>
              
              {/* Protected Routes */}
              <Route element={<MainLayout />}>
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/calendar" element={<CalendarPage />} />
                {/* Phase 26.8.1 — Internal Events tabular list. Moved from
                    /events (which is now exclusively the public marketing
                    page) to /portal/events to avoid the route conflict. */}
                <Route path="/portal/events" element={<EventsListPage />} />
                <Route path="/portal/events/:eventId" element={<CalendarPage />} />
                <Route path="/events-calendar/dashboard" element={<EventsCalendarDashboardPage />} />
                <Route path="/recognition" element={<RecognitionPortalPage />} />
                <Route path="/recognition/admin" element={<RecognitionAdminPage />} />
                <Route path="/recognition/admin/:id" element={<RecognitionProgramEditorPage />} />
                <Route path="/recognition/:id" element={<RecognitionProgramPage />} />
                {/* Phase 27.30.11 — Event Workspace (Wix-style tabs). */}
                <Route path="/events/:id/workspace" element={<EventWorkspacePage />} />
                <Route path="/agencies" element={<AgenciesPage />} />
                <Route path="/agencies/:agencyId" element={<AgencyProfilePage />} />
                <Route path="/agents" element={<AgentsPage />} />
                <Route path="/agents/:npn" element={<AgentProfilePage />} />
                <Route path="/requests" element={<RequestsPage />} />
                <Route path="/requests/:id" element={<RequestDetailPage />} />
                {/* Phase 24.2 — unified terminology aliases (same page, same data) */}
                <Route path="/contracts" element={<RequestsPage />} />
                <Route path="/contracts/import-status" element={<ContractStatusImportPage />} />
                <Route path="/contracts/import-status/review" element={<ContractStatusReviewPage />} />
                <Route path="/contracts/:id" element={<RequestDetailPage />} />
                <Route path="/release-followups" element={<ReleaseFollowUpsPage />} />
                {/* Phase 27.30.7 — legacy notification links `/followups/:id`
                    had no matching route and fell through to the catch-all,
                    which sent users to the landing page. Redirect to the
                    dashboard where the My Follow-ups widget lives. */}
                <Route path="/followups/:id" element={<Navigate to="/dashboard" replace />} />
                <Route path="/followups" element={<Navigate to="/dashboard" replace />} />
                <Route path="/request-contract" element={<RequestContractPage />} />
                <Route path="/catalog" element={<KrOnlyRoute><CatalogPage /></KrOnlyRoute>} />
                <Route path="/level-caps" element={<LevelCapsPage />} />
                <Route path="/tickets" element={<TicketsPage />} />
                <Route path="/invitations" element={<InvitationsPage />} />
                <Route path="/onboarding-workflows" element={<OnboardingWorkflowsPage />} />
                <Route path="/urgent-tasks" element={<UrgentTasksPage />} />
                <Route path="/action-required" element={<ActionRequiredPage />} />
                {/* Phase 27.37.Y — /my-contracts consolidated into
                    /contracts (the canonical page with full detail).
                    Kept as a redirect so any bookmarks or emails still
                    land the agent on the right screen. */}
                <Route path="/my-contracts" element={<Navigate to="/contracts" replace />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/announcements" element={<AnnouncementsPage />} />
                <Route path="/users" element={<UsersPage />} />
                <Route path="/admin/jarvis-keys" element={<JarvisKeysPage />} />
                <Route path="/admin/orphan-carriers" element={<OrphanCarriersPage />} />
                <Route path="/admin/aep-certifications" element={<AepCertificationsAdminPage />} />
                <Route path="/birthdays" element={<BirthdaysPage />} />
                <Route path="/audit-log" element={<AuditLogPage />} />
                <Route path="/admin/contract-duplicates" element={<ContractDuplicateReviewPage />} />

                {/* Phase 25.1 — Accounting foundation */}
                <Route path="/accounting" element={<PaymentCenterPage />} />
                <Route path="/accounting/payment-center" element={<PaymentCenterPage />} />
                <Route path="/accounting/carrier-imports" element={<CarrierImportsPage />} />
                <Route path="/accounting/bank-deposits" element={<BankDepositsPage />} />
                <Route path="/accounting/commission-schedules" element={<CommissionSchedulesPage />} />
                <Route path="/accounting/unreceived-commission" element={<UnreceivedCommissionPage />} />
                <Route path="/accounting/carrier-imports/:id" element={<CarrierImportDetailPage />} />
                <Route path="/accounting/statements" element={<StatementsListPage />} />
                <Route path="/accounting/statements/:id" element={<StatementDetailPage />} />
                <Route path="/accounting/manual-payments" element={<Navigate to="/accounting/manual-adjustments" replace />} />
                <Route path="/accounting/manual-adjustments" element={<ManualAdjustmentsListPage />} />
                <Route path="/accounting/payment-batches" element={<PaymentBatchesListPage />} />
                <Route path="/accounting/payment-batches/:batchId" element={<PaymentBatchDetailPage />} />
                <Route path="/accounting/ledger" element={<LedgerExplorerPage />} />
                <Route path="/accounting/history" element={<AccountingHistoryPage />} />
                <Route path="/accounting/reimbursements" element={<AccountingRoute><ReimbursementsAdminPage /></AccountingRoute>} />
                <Route path="/reimbursements" element={<MyReimbursementsPage />} />
                <Route path="/governance/communication-policy" element={<CommunicationGovernancePage />} />
                <Route path="/platform-settings" element={<PlatformSettingsPage />} />
                <Route path="/settings/integrations/twilio" element={<TwilioIntegrationPage />} />
                <Route path="/settings/voice" element={<VoiceSettingsPage />} />
                <Route path="/call-history" element={<CallHistoryPage />} />
                <Route path="/settings/voice/supervision" element={<VoiceSupervisionPage />} />
                <Route path="/settings/voice/usage" element={<VoiceUsagePage />} />
                <Route path="/settings/voice/flows" element={<CallFlowsPage />} />
                <Route path="/communication-log" element={<CommunicationLogPage />} />
                <Route path="/voicemails" element={<VoicemailInboxPage />} />
                <Route path="/academy" element={<AcademyPage />} />
                <Route path="/academy/manage/:courseId" element={<CourseBuilderPage />} />
                <Route path="/academy/certificate/:certId" element={<CertificatePage />} />
                <Route path="/academy/:courseId" element={<CoursePlayerPage />} />
                <Route path="/settings/integrations/quickbooks" element={<QuickBooksIntegrationPage />} />
                <Route path="/sms-inbox" element={<SmsInboxPage />} />
                <Route path="/sms-broadcasts" element={<SmsBroadcastsPage />} />
                <Route path="/reports" element={<ReportsPage />} />
                <Route path="/operations" element={<OperationsPage />} />
                <Route path="/notification-settings" element={<NotificationPreferencesPage />} />
                <Route path="/accounting/ledger" element={<LedgerPage />} />
                <Route path="/accounting/statements" element={<StatementsPage />} />
                <Route path="/accounting/payments" element={<PaymentsPage />} />
                <Route path="/accounting/reconciliation" element={<ReconciliationPage />} />
                <Route path="/accounting/eft" element={<EFTPage />} />
                <Route path="/accounting/override" element={<OverrideReconciliationPage />} />
                <Route path="/accounting/compensation" element={<CompensationPage />} />
                <Route path="/managers" element={<ManagersPage />} />
                <Route path="/my-payouts" element={<MyPayoutsPage />} />
                <Route path="/kanban" element={<KanbanBoardPage />} />
                <Route path="/trainees" element={<TraineesPage />} />
                <Route path="/marketing" element={<MarketingPage />} />
                <Route path="/knowledge" element={<KnowledgePage />} />
                <Route path="/team" element={<TeamPage />} />
                <Route path="/team/:user_id" element={<TeamMemberProfilePage />} />
                <Route path="/marketing/new" element={<MarketingTemplateEditor />} />
                <Route path="/marketing/requests" element={<MarketingRequestsPage />} />
                <Route path="/marketing/:id/edit" element={<MarketingTemplateEditor />} />
                <Route path="/marketing/:id/personalize" element={<MarketingPersonalize />} />
                {/* Phase 27.50.B — Storefront + Cart */}
                <Route path="/shop" element={<ShopPage />} />
                <Route path="/cart" element={<CartPage />} />
                <Route path="/checkout/cart" element={<CartCheckoutPage />} />
                <Route path="/checkout/afterpay-return" element={<AfterpayReturnPage />} />
                <Route path="/my-orders" element={<MyOrdersPage />} />
                <Route path="/special-orders/:id/pay" element={<SpecialOrderPayPage />} />
                <Route path="/admin/storefront" element={<AdminStorefrontPage />} />
                <Route path="/admin/disclaimer" element={<KrOnlyRoute><DisclaimerConfigPage /></KrOnlyRoute>} />
                <Route path="/admin/deletion-log" element={<AdminDeletionLogPage />} />
                <Route path="/prospects" element={<ProspectsPage />} />
                <Route path="/referrals" element={<ReferralsPage />} />
                <Route path="/settings/auto-reminders" element={<AutoReminderSettingsPage />} />
                <Route path="/settings/seasonal-themes" element={<KrOnlyRoute><SeasonalThemesPage /></KrOnlyRoute>} />
                <Route path="/kora/analytics" element={<KoraAnalyticsPage />} />
                <Route path="/kora/training" element={<KrOnlyRoute><KoraTrainingPage /></KrOnlyRoute>} />
                <Route path="/leads" element={<LeadsPage />} />
                <Route path="/clients" element={<ClientsPage />} />
                <Route path="/clients/:id" element={<ClientDetailPage />} />
                <Route path="/leads/appointments" element={<AppointmentsPage />} />
                <Route path="/leads/landing-pages" element={<LeadLandingPagesPage />} />
                <Route path="/leads/landing-pages/consent" element={<LandingConsentAdminPage />} />
                <Route path="/leads/landing-pages/new" element={<LeadLandingPageEditor />} />
                <Route path="/leads/landing-pages/:id" element={<LeadLandingPageEditor />} />
                <Route path="/my-landing-page" element={<LeadLandingPagesPage mine />} />
                <Route path="/my-landing-page/new" element={<LeadLandingPageEditor mine />} />
                <Route path="/my-landing-page/:id" element={<LeadLandingPageEditor mine />} />
                <Route path="/leads/import" element={<LeadsImportPage />} />
                <Route path="/leads/reports" element={<LeadsReportsPage />} />
                <Route path="/leads/:id" element={<LeadDetailPage />} />
              </Route>
              
              {/* Fallback — send unknown routes to the public home */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            {/* Phase 27.33 — Global enrollment platforms launcher.
                Rendered inside <BrowserRouter> so it can use routing
                hooks; the component itself hides on public routes
                and when the user is signed out. */}
            <EnrollmentPlatformsLauncher />
            {/* Phase 27.50.C.37 — Confidentiality / copyright
                acknowledgment popup. Shown once every 5 days to any
                gated external user (Agent / Agency_Principal /
                Agency_User) after they log in. Rendered inside
                <BrowserRouter> so it lives alongside the app. */}
            <DisclaimerGateWithAuth />
          </BrowserRouter>
          <Toaster position="top-right" />
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
